import { Alert, Stack, Typography } from '@mui/material';
import React, { forwardRef, Ref } from 'react';
import { useFormContext, UseFormReturn } from 'react-hook-form';

import { scrollTargetStyle } from '../../constants/style';
import useAdGroupLocations from '../../hooks/useAdGroupLocations';
import { AdGroup } from '../../validationSchemas/AdGroupSchema';
import Checkbox from '../forms/Checkbox';
import FormSection from '../forms/FormSection';
import CollapsiblePaper from '../shared/CollapsiblePaper';
import LocationListsTable from './TargetingLocationLists/LocationListsTable';
import DestinationsTable from './TargetingLocations/DestinationsTable';
import ExcludedLocations from './TargetingLocations/ExcludedLocations';
import LocationRadii from './TargetingLocations/LocationRadii';
import LocationTable from './TargetingLocations/LocationTable';

function AdGroupLocationsSection(_props: object, ref: Ref<HTMLDivElement>) {
  const { getValues, watch }: UseFormReturn<AdGroup> = useFormContext();
  const conquest = watch('conquest');
  const [adGroupId, locationsCount] = getValues(['id', 'locationsCount']);

  // An ad group can only have locations lists or locations directly, not both.
  // Thus `locationsCount` will have the total number of included locations in the ad group (in locations lists or as locations directly)
  // We need to show the total number of locations including excluded locations as well
  const { data: excludedLocations, isSuccess: isExcludedLocationsSuccess } = useAdGroupLocations(
    adGroupId,
    true,
    '',
    9999999
  );

  let totalLocations = locationsCount;
  totalLocations += (isExcludedLocationsSuccess && excludedLocations?.pagination?.total_items) || 0;

  return (
    <FormSection
      title={`${conquest ? 'Conquest ' : ''}Locations (${totalLocations})`}
      subTitle="I'd like to control my target inventory based on..."
    >
      <div ref={ref} style={scrollTargetStyle} />
      <Stack spacing={1}>
        {adGroupId ? (
          <Stack spacing={1}>
            <LocationListsTable />
            <CollapsiblePaper
              sessionKey="locations"
              title={`${conquest ? 'Conquest ' : ''}Locations (${totalLocations > 0 ? totalLocations : 0})`}
            >
              <Stack spacing={2}>
                <Stack>
                  <Typography>Type</Typography>
                  <Checkbox label="Conquest (Many locations = No)" name="conquest" />
                </Stack>
                <LocationRadii />
                <DestinationsTable />
                <LocationTable />
                <ExcludedLocations />
              </Stack>
            </CollapsiblePaper>
          </Stack>
        ) : (
          <Alert severity="warning">Save this Ad Group in order to add Locations</Alert>
        )}
      </Stack>
    </FormSection>
  );
}

export default React.memo(forwardRef(AdGroupLocationsSection));
